import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@barges/react';
import { useDispatch } from 'react-redux';
import { appRoutes } from '@app/config/app_routes';
import { updateUserAPI } from '../../../common/api/api';
import Const from '../../config/const';
import { log, LOG_LEVELS } from '../../../common/config/app_logger';
import { SvgIcon } from '../../../common/sprites';
import classes from './classes.module.scss';
import { useCurrentPath } from '../routes/hooks/useCurrentPath';

function FxMigrationAlert({ user }: { user: any }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [documentComplete, setDocumentComplete] = useState(
    () => typeof document !== 'undefined' && document.readyState === 'complete'
  );

  // BroadcastChannel for communication with other tabs
  const sendBroadcastEvent = (currency: string) => {
    if (typeof BroadcastChannel === 'undefined') {
      log(LOG_LEVELS.INFO, 'BroadcastChannel', 'BroadcastChannel is not supported in this browser');
      window.location.reload();
    } else {
      const bc = new BroadcastChannel('currency_changed');
      bc.postMessage({ currency });
    }
  };

  useEffect(() => {
    const markComplete = () => {
      if (document.readyState === 'complete') {
        setDocumentComplete(true);
      }
    };
    markComplete();
    document.addEventListener('readystatechange', markComplete);
    window.addEventListener('load', markComplete, { once: true });
    return () => {
      document.removeEventListener('readystatechange', markComplete);
      window.removeEventListener('load', markComplete);
    };
  }, []);

  const { routeKey } = useCurrentPath();
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);

  const suggestedCurrency = user?.suggestedCurrency;

  const shouldShow = user != null
    && suggestedCurrency != null
    && user.fxMigrationCompleted === false
    && suggestedCurrency !== user.currency
    && ![appRoutes.purchaseDetails, appRoutes.advisorBonus].includes(routeKey)
    && !dismissed
    && documentComplete;

  const handleConfirm = useCallback(() => {
    if (loading) return;
    setLoading(true);

    updateUserAPI({ currency: suggestedCurrency }).then(() => {
      dispatch({ type: Const.user.updateUserData, user: { currency: suggestedCurrency } });
      sendBroadcastEvent(suggestedCurrency);
      setDismissed(true);
    })
      .catch((error: any) => {
        log(LOG_LEVELS.ERROR, 'FxMigrationAlert', 'Error updating currency', error);
        setDismissed(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [suggestedCurrency, loading, dispatch]);

  if (!shouldShow) return null;

  return (
    <Popover
      isOpen={ shouldShow }
      centered
      contentClassName={ classes.cropperPopupContent }
    >
      <div className={ classes.container }>
        <div className={ classes.content }>
          <div className={ classes.titleSection }>
            <SvgIcon id="confetti" className={ classes.confettiImg } />
            <div className={ classes.title }>
              { t('fx_migration_alert.title') }
            </div>
          </div>
          <div className={ classes.description }>
            { t('fx_migration_alert.description') }
          </div>
        </div>
        <button
          type="button"
          className={ classes.button }
          onClick={ handleConfirm }
          disabled={ loading }
        >
          { t('fx_migration_alert.button') }
        </button>
      </div>
    </Popover>
  );
}

export default FxMigrationAlert;
